import { assign, createMachine } from 'xstate';
import { AppStateErrorType, ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { CredentialsService, credentialsService } from '../../services/credentialsService';
import {
  Credentials,
  CredentialsEntity,
  CredentialsStatus,
  CredentialsType,
} from '../../../models/credentials';
import { credentialsTimeoutError, POLL_DELAY, POLL_INTERVAL, POLL_TIMEOUT } from '../utils/utils';
import { ProviderEntity } from '../../../models/provider';
import { isSwedbankOrSavingsbank } from '../../../payments/legacy/components/PaymentDirector/molecules/SwedbankSavingsbankErrorOverlay/SwedbankSavingsbankErrorOverlay';
import { Typegen0 } from './credentialsMachine.typegen';

export type CredentialsMachineEvent = {
  type: 'MACHINE_DONE_EVENT';
  credentials: CredentialsEntity;
};

export type CredentialsMachineService = {
  getCredentials: { data: CredentialsEntity };
};

export type CredentialsMachineContext = {
  credentials: CredentialsEntity;
  error?: ApplicationError;
};

export type CredentialsMachineProps = {
  initialContext: CredentialsMachineContext;
  provider: ProviderEntity;
  onDone: (updatedCredentials: CredentialsEntity) => void;
  onAuthError: (errorCredentials: CredentialsEntity) => void;
  waitForUpdated: boolean;
};

export type CredentialsMachineTestOverrides = {
  initialState?: Exclude<Typegen0['matchesStates'], object>;
  service?: CredentialsService;
  pollInterval?: number;
  pollTimeout?: number;
  pollDelay?: number;
};

export const createCredentialsMachine = ({
  initialContext,
  initialState = 'POLL_CREDENTIALS',
  onDone,
  onAuthError,
  provider,
  waitForUpdated,
  service = credentialsService,
  pollInterval = POLL_INTERVAL,
  pollTimeout = POLL_TIMEOUT,
  pollDelay = POLL_DELAY,
}: CredentialsMachineProps & CredentialsMachineTestOverrides) =>
  createMachine(
    {
      id: 'CredentialsMachine',
      initial: initialState,
      context: initialContext,
      tsTypes: {} as import('./credentialsMachine.typegen').Typegen0,
      schema: {
        context: {} as CredentialsMachineContext,
        events: {} as CredentialsMachineEvent,
        services: {} as CredentialsMachineService,
      },
      predictableActionArguments: true,
      states: {
        POLL_CREDENTIALS: {
          after: {
            POLL_TIMEOUT: [
              {
                target: '#CredentialsMachine.APPLICATION_ERROR_VIEW',
                actions: ['set_polling_timeout_error_in_context'],
              },
            ],
          },
          initial: 'DELAY',
          states: {
            DELAY: {
              after: {
                POLL_DELAY: [
                  {
                    target: '#CredentialsMachine.POLL_CREDENTIALS.GET_CREDENTIALS',
                  },
                ],
              },
            },
            GET_CREDENTIALS: {
              invoke: {
                src: 'getCredentials',
                onDone: [
                  {
                    target: 'AWAITING',
                    actions: 'update_credentials_in_context',
                  },
                ],
                onError: [
                  {
                    target: '#CredentialsMachine.APPLICATION_ERROR_VIEW',
                    actions: 'set_error_in_context',
                  },
                ],
              },
            },
            AWAITING: {
              after: {
                POLL_INTERVAL: [
                  {
                    target: '#CredentialsMachine.POLL_CREDENTIALS.GET_CREDENTIALS',
                  },
                ],
              },
            },
          },
          always: [
            {
              target: 'AWAITING_ACCOUNTS_SELECTION_MACHINE',
              cond: 'check_if_awaiting_accounts_selection',
            },
            {
              target: 'SUPPLEMENTAL_INFORMATION_MACHINE',
              cond: 'check_if_awaiting_supplemental_information',
            },
            {
              target: 'BANKID_MACHINE',
              cond: 'check_if_awaiting_bank_id',
            },
            {
              target: 'THIRD_PARTY_REDIRECT_MACHINE',
              cond: 'check_if_awaiting_tpp_redirect',
            },
            {
              target: 'CREDENTIALS_ERROR',
              cond: 'check_if_credentials_error',
            },
            {
              target: 'CREDENTIALS_UPDATED',
              cond: 'check_if_credentials_updated_or_updating',
            },
          ],
        },
        AWAITING_ACCOUNTS_SELECTION_MACHINE: {
          on: {
            MACHINE_DONE_EVENT: {
              target: 'POLL_CREDENTIALS',
              actions: 'set_credentials_in_context',
            },
          },
        },
        SUPPLEMENTAL_INFORMATION_MACHINE: {
          on: {
            MACHINE_DONE_EVENT: {
              target: 'POLL_CREDENTIALS',
              actions: 'set_credentials_in_context',
            },
          },
        },
        BANKID_MACHINE: {
          on: {
            MACHINE_DONE_EVENT: [
              {
                target: 'POLL_CREDENTIALS',
                actions: 'set_credentials_in_context',
              },
            ],
          },
        },
        THIRD_PARTY_REDIRECT_MACHINE: {
          type: 'final',
        },
        CREDENTIALS_ERROR: {
          always: [
            {
              target: 'APPLICATION_ERROR_VIEW',
              cond: 'check_if_credentials_fatal_error',
              actions: 'throw_credentials_application_error',
            },
            {
              target: 'AUTH_ERROR',
            },
          ],
        },
        CREDENTIALS_UPDATED: {
          entry: 'execute_on_done',
          type: 'final',
        },
        APPLICATION_ERROR_VIEW: {
          type: 'final',
        },
        AUTH_ERROR: {
          entry: 'execute_on_auth_error',
          type: 'final',
        },
      },
    },
    {
      guards: {
        check_if_awaiting_bank_id: ({ credentials }) =>
          Credentials.isAwaitingMobileBankId(credentials),
        check_if_credentials_error: ({ credentials }) => Credentials.hasError(credentials),
        check_if_credentials_updated_or_updating: ({ credentials }) =>
          Credentials.isUpdated(credentials) ||
          (Credentials.isUpdating(credentials) && !waitForUpdated),
        check_if_awaiting_tpp_redirect: ({ credentials }) =>
          Credentials.isAwaitingThirdPartyAppAuthentication(credentials) &&
          !!credentials.supplementalInformation,
        check_if_awaiting_accounts_selection: ({ credentials }) =>
          Credentials.isAwaitingAccountsSelection(credentials),
        check_if_awaiting_supplemental_information: ({ credentials }) =>
          Credentials.isAwaitingSupplementalInformation(credentials),
        check_if_credentials_fatal_error: ({ credentials }) => {
          const { credentialsType } = provider;
          const isSwedbankError =
            credentials &&
            isSwedbankOrSavingsbank(credentials.providerName) &&
            Credentials.isSwedbankSavingsbankError(credentials);
          const isFatalCredentialsType = credentialsType
            ? [CredentialsType.MOBILE_BANKID, CredentialsType.THIRD_PARTY_APP].includes(
                credentialsType
              )
            : false;

          return Boolean(isFatalCredentialsType && !isSwedbankError);
        },
      },
      actions: {
        set_credentials_in_context: assign({ credentials: (_, event) => event.credentials }),
        update_credentials_in_context: assign({ credentials: (_, event) => event.data }),
        set_error_in_context: assign({ error: (_, event) => event.data as ApplicationError }),
        set_polling_timeout_error_in_context: assign({
          error: (_) => new ApplicationError(credentialsTimeoutError),
        }),
        execute_on_done: ({ credentials }) => {
          onDone(credentials);
        },
        execute_on_auth_error: ({ credentials }) => {
          onAuthError(credentials);
        },
        throw_credentials_application_error: assign({
          error: ({ credentials }) => {
            const {
              detailedError: credentialsDetailedError,
              id: credentialsId,
              status,
              providerName,
            } = credentials;

            if (status === CredentialsStatus.TEMPORARY_ERROR) {
              return new ApplicationError({
                errorStatus: ErrorStatus.TEMPORARY_ERROR,
                providerName,
                credentialsDetailedError,
                errorReason: ErrorReason.INVALID_STATE_CREDENTIALS,
              });
            }

            return new ApplicationError({
              errorType: AppStateErrorType.CREDENTIALS,
              errorStatus: ErrorStatus.AUTHENTICATION_ERROR,
              credentialsId,
              providerName,
              credentialsDetailedError,
              errorReason: ErrorReason.INVALID_STATE_AUTHENTICATION,
            });
          },
        }),
      },
      services: {
        getCredentials: ({ credentials }) => service.getCredentials(credentials.id),
      },
      delays: {
        POLL_INTERVAL: pollInterval,
        POLL_TIMEOUT: pollTimeout,
        POLL_DELAY: pollDelay,
      },
    }
  );
